import { useState } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, FileText, Link as LinkIcon, Eye, Edit, Trash2, BookOpen, FileImage, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter, 
  DialogTrigger,
  DialogDescription
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

// Content types
interface LearningMaterial {
  id: string;
  title: string;
  description: string;
  contentType: "article" | "video" | "quiz" | "document";
  category: string;
  url: string;
  thumbnail?: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ResourceLink {
  id: string;
  title: string;
  description: string;
  url: string;
  category: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

const ContentManagement = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("learning-materials");
  const [materialSearchTerm, setMaterialSearchTerm] = useState("");
  const [linkSearchTerm, setLinkSearchTerm] = useState("");
  const [isAddMaterialDialogOpen, setIsAddMaterialDialogOpen] = useState(false);
  const [isAddLinkDialogOpen, setIsAddLinkDialogOpen] = useState(false);

  const [newMaterial, setNewMaterial] = useState({
    title: "",
    description: "",
    contentType: "article" as "article" | "video" | "quiz" | "document",
    category: "beginner",
    url: "",
    published: true,
  });

  const [newLink, setNewLink] = useState({
    title: "",
    description: "",
    url: "",
    category: "general",
    published: true,
  });

  // Sample learning materials
  const sampleMaterials: LearningMaterial[] = [
    {
      id: "1",
      title: "Introduction to Driving",
      description: "Basics of vehicle controls and safety",
      contentType: "article",
      category: "beginner",
      url: "/learning/intro-driving",
      thumbnail: "/placeholder.svg",
      published: true,
      createdAt: "2025-03-10T10:00:00Z",
      updatedAt: "2025-03-15T14:30:00Z",
    },
    {
      id: "2",
      title: "Highway Driving Techniques",
      description: "Advanced techniques for safe highway driving",
      contentType: "video",
      category: "intermediate",
      url: "https://example.com/videos/highway-driving",
      thumbnail: "/placeholder.svg",
      published: true,
      createdAt: "2025-03-12T11:20:00Z",
      updatedAt: "2025-03-12T11:20:00Z",
    },
    {
      id: "3",
      title: "Parallel Parking Guide",
      description: "Step-by-step instructions for perfect parallel parking",
      contentType: "document",
      category: "beginner",
      url: "/docs/parallel-parking.pdf",
      published: true,
      createdAt: "2025-03-15T09:45:00Z",
      updatedAt: "2025-03-16T16:10:00Z",
    },
    {
      id: "4",
      title: "Theory Test Practice Quiz",
      description: "Practice questions for the driving theory test",
      contentType: "quiz",
      category: "test-prep",
      url: "/quizzes/theory-test",
      published: true,
      createdAt: "2025-03-18T13:30:00Z",
      updatedAt: "2025-03-18T13:30:00Z",
    },
  ];

  // Sample resource links
  const sampleLinks: ResourceLink[] = [
    {
      id: "1",
      title: "Official DMV Website",
      description: "Government resource for driving regulations",
      url: "https://dmv.gov",
      category: "government",
      published: true,
      createdAt: "2025-03-05T10:00:00Z",
      updatedAt: "2025-03-05T10:00:00Z",
    },
    {
      id: "2",
      title: "Traffic Rules Handbook",
      description: "Comprehensive guide to traffic rules and regulations",
      url: "https://trafficrules.org",
      category: "resources",
      published: true,
      createdAt: "2025-03-10T11:20:00Z",
      updatedAt: "2025-03-10T11:20:00Z",
    },
    {
      id: "3",
      title: "Defensive Driving Techniques",
      description: "Learn defensive driving to stay safe on the road",
      url: "https://defensivedriving.com",
      category: "safety",
      published: true,
      createdAt: "2025-03-15T09:45:00Z",
      updatedAt: "2025-03-15T09:45:00Z",
    },
  ];

  // In a real app, you would load content from localStorage or an API
  const getMaterials = (): LearningMaterial[] => {
    const storedMaterials = localStorage.getItem("learningMaterials");
    return storedMaterials ? JSON.parse(storedMaterials) : sampleMaterials;
  };

  const getLinks = (): ResourceLink[] => {
    const storedLinks = localStorage.getItem("resourceLinks");
    return storedLinks ? JSON.parse(storedLinks) : sampleLinks;
  };

  const materials = getMaterials();
  const links = getLinks();

  const filteredMaterials = materials.filter(
    (material) =>
      material.title.toLowerCase().includes(materialSearchTerm.toLowerCase()) ||
      material.description.toLowerCase().includes(materialSearchTerm.toLowerCase()) ||
      material.category.toLowerCase().includes(materialSearchTerm.toLowerCase())
  );

  const filteredLinks = links.filter(
    (link) =>
      link.title.toLowerCase().includes(linkSearchTerm.toLowerCase()) ||
      link.description.toLowerCase().includes(linkSearchTerm.toLowerCase()) ||
      link.category.toLowerCase().includes(linkSearchTerm.toLowerCase())
  );

  const handleMaterialInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    setNewMaterial({
      ...newMaterial,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleLinkInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    setNewLink({
      ...newLink,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleAddMaterial = () => {
    // Validate form
    if (!newMaterial.title || !newMaterial.description || !newMaterial.url) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Create new material
    const newMaterialData: LearningMaterial = {
      ...newMaterial,
      id: crypto.randomUUID(),
      thumbnail: "/placeholder.svg",
      published: newMaterial.published,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Add to localStorage
    localStorage.setItem("learningMaterials", JSON.stringify([...materials, newMaterialData]));

    // Reset form and close dialog
    setNewMaterial({
      title: "",
      description: "",
      contentType: "article",
      category: "beginner",
      url: "",
      published: true,
    });
    setIsAddMaterialDialogOpen(false);

    toast({
      title: "Success",
      description: "Learning material added successfully",
    });
  };

  const handleAddLink = () => {
    // Validate form
    if (!newLink.title || !newLink.url) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Create new link
    const newLinkData: ResourceLink = {
      ...newLink,
      id: crypto.randomUUID(),
      published: newLink.published,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Add to localStorage
    localStorage.setItem("resourceLinks", JSON.stringify([...links, newLinkData]));

    // Reset form and close dialog
    setNewLink({
      title: "",
      description: "",
      url: "",
      category: "general",
      published: true,
    });
    setIsAddLinkDialogOpen(false);

    toast({
      title: "Success",
      description: "Resource link added successfully",
    });
  };

  const handleDeleteMaterial = (materialId: string) => {
    if (confirm("Are you sure you want to delete this learning material?")) {
      const updatedMaterials = materials.filter(material => material.id !== materialId);
      localStorage.setItem("learningMaterials", JSON.stringify(updatedMaterials));
      
      toast({
        title: "Success",
        description: "Learning material deleted successfully",
      });
    }
  };

  const handleDeleteLink = (linkId: string) => {
    if (confirm("Are you sure you want to delete this resource link?")) {
      const updatedLinks = links.filter(link => link.id !== linkId);
      localStorage.setItem("resourceLinks", JSON.stringify(updatedLinks));
      
      toast({
        title: "Success",
        description: "Resource link deleted successfully",
      });
    }
  };

  const handleTogglePublishMaterial = (materialId: string, currentStatus: boolean) => {
    const updatedMaterials = materials.map(material => 
      material.id === materialId 
        ? { ...material, published: !currentStatus, updatedAt: new Date().toISOString() } 
        : material
    );
    localStorage.setItem("learningMaterials", JSON.stringify(updatedMaterials));
    
    toast({
      title: "Success",
      description: `Material ${currentStatus ? 'unpublished' : 'published'} successfully`,
    });
  };

  const handleTogglePublishLink = (linkId: string, currentStatus: boolean) => {
    const updatedLinks = links.map(link => 
      link.id === linkId 
        ? { ...link, published: !currentStatus, updatedAt: new Date().toISOString() } 
        : link
    );
    localStorage.setItem("resourceLinks", JSON.stringify(updatedLinks));
    
    toast({
      title: "Success",
      description: `Link ${currentStatus ? 'unpublished' : 'published'} successfully`,
    });
  };

  return (
    <DashboardLayout role="admin">
      <div className="mb-8 space-y-4">
        <h1 className="text-3xl font-bold text-gray-900">Content Management</h1>
        <p className="text-gray-600">
          Manage learning materials, resources, and content for students.
        </p>
      </div>

      <Tabs defaultValue="learning-materials" className="space-y-6" onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="learning-materials">Learning Materials</TabsTrigger>
          <TabsTrigger value="resource-links">Resource Links</TabsTrigger>
        </TabsList>

        <TabsContent value="learning-materials" className="space-y-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center w-full max-w-sm space-x-2">
              <Input
                placeholder="Search materials..."
                value={materialSearchTerm}
                onChange={(e) => setMaterialSearchTerm(e.target.value)}
                className="flex-1"
              />
              <Button variant="outline" size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            <Dialog open={isAddMaterialDialogOpen} onOpenChange={setIsAddMaterialDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Material
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add Learning Material</DialogTitle>
                  <DialogDescription>
                    Create new learning material for students. Fill in the required fields marked with an asterisk (*).
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title*</Label>
                    <Input
                      id="title"
                      name="title"
                      value={newMaterial.title}
                      onChange={handleMaterialInputChange}
                      placeholder="Introduction to Safe Driving"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">Description*</Label>
                    <textarea
                      id="description"
                      name="description"
                      value={newMaterial.description}
                      onChange={handleMaterialInputChange}
                      placeholder="A comprehensive introduction to safe driving practices..."
                      className="w-full h-20 px-3 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contentType">Content Type*</Label>
                      <select
                        id="contentType"
                        name="contentType"
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        value={newMaterial.contentType}
                        onChange={handleMaterialInputChange}
                      >
                        <option value="article">Article</option>
                        <option value="video">Video</option>
                        <option value="quiz">Quiz</option>
                        <option value="document">Document</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Category*</Label>
                      <select
                        id="category"
                        name="category"
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        value={newMaterial.category}
                        onChange={handleMaterialInputChange}
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                        <option value="test-prep">Test Preparation</option>
                        <option value="safety">Safety</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="url">URL / Content Link*</Label>
                    <Input
                      id="url"
                      name="url"
                      value={newMaterial.url}
                      onChange={handleMaterialInputChange}
                      placeholder="https://example.com/content or /learning/article-slug"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="published"
                      name="published"
                      checked={newMaterial.published}
                      onChange={handleMaterialInputChange}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <Label htmlFor="published">Publish immediately</Label>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddMaterialDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddMaterial}>Add Material</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {filteredMaterials.length > 0 ? (
              filteredMaterials.map((material) => (
                <Card key={material.id} className="overflow-hidden">
                  <div className="h-40 bg-gray-100 relative">
                    {material.thumbnail && (
                      <img
                        src={material.thumbnail}
                        alt={material.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute top-2 right-2 flex gap-2">
                      <Badge 
                        className={material.published ? "bg-green-500" : "bg-gray-500"}
                      >
                        {material.published ? "Published" : "Draft"}
                      </Badge>
                      <Badge 
                        variant="outline" 
                        className="bg-white text-gray-700 border-gray-300"
                      >
                        {material.contentType}
                      </Badge>
                    </div>
                  </div>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{material.title}</CardTitle>
                    </div>
                    <Badge variant="outline">{material.category}</Badge>
                  </CardHeader>
                  <CardContent className="pb-3">
                    <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                      {material.description}
                    </p>
                    <div className="flex justify-between items-center">
                      <div className="text-xs text-gray-500">
                        Updated: {new Date(material.updatedAt).toLocaleDateString()}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleTogglePublishMaterial(material.id, material.published)}
                        >
                          {material.published ? (
                            <BookOpen className="h-4 w-4 text-green-600" />
                          ) : (
                            <BookOpen className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteMaterial(material.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                <FileText className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No materials found</h3>
                <p className="text-gray-500 mt-2 max-w-md">
                  No learning materials match your search criteria. Try adjusting your search or add new materials.
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="resource-links" className="space-y-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center w-full max-w-sm space-x-2">
              <Input
                placeholder="Search links..."
                value={linkSearchTerm}
                onChange={(e) => setLinkSearchTerm(e.target.value)}
                className="flex-1"
              />
              <Button variant="outline" size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            <Dialog open={isAddLinkDialogOpen} onOpenChange={setIsAddLinkDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Add Link
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Resource Link</DialogTitle>
                  <DialogDescription>
                    Add a new external resource link for students. Fill in the required fields marked with an asterisk (*).
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="link-title">Title*</Label>
                    <Input
                      id="link-title"
                      name="title"
                      value={newLink.title}
                      onChange={handleLinkInputChange}
                      placeholder="Official DMV Website"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="link-description">Description</Label>
                    <Input
                      id="link-description"
                      name="description"
                      value={newLink.description}
                      onChange={handleLinkInputChange}
                      placeholder="Official government website for driving regulations"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="link-url">URL*</Label>
                    <Input
                      id="link-url"
                      name="url"
                      value={newLink.url}
                      onChange={handleLinkInputChange}
                      placeholder="https://example.com"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="link-category">Category*</Label>
                    <select
                      id="link-category"
                      name="category"
                      className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      value={newLink.category}
                      onChange={handleLinkInputChange}
                    >
                      <option value="general">General</option>
                      <option value="government">Government</option>
                      <option value="resources">Resources</option>
                      <option value="safety">Safety</option>
                      <option value="test-prep">Test Preparation</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="link-published"
                      name="published"
                      checked={newLink.published}
                      onChange={handleLinkInputChange}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <Label htmlFor="link-published">Publish immediately</Label>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddLinkDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddLink}>Add Link</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {filteredLinks.length > 0 ? (
              filteredLinks.map((link) => (
                <Card key={link.id}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{link.title}</CardTitle>
                      <Badge 
                        className={link.published ? "bg-green-500" : "bg-gray-500"}
                      >
                        {link.published ? "Published" : "Draft"}
                      </Badge>
                    </div>
                    <Badge variant="outline">{link.category}</Badge>
                  </CardHeader>
                  <CardContent className="pb-3">
                    <p className="text-sm text-gray-600 mb-4">
                      {link.description || "No description provided."}
                    </p>
                    <div className="flex items-center mb-4">
                      <LinkIcon className="h-4 w-4 mr-2 text-blue-500" />
                      <a 
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-sm text-blue-500 hover:underline truncate"
                      >
                        {link.url}
                      </a>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-xs text-gray-500">
                        Updated: {new Date(link.updatedAt).toLocaleDateString()}
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleTogglePublishLink(link.id, link.published)}
                        >
                          {link.published ? (
                            <BookOpen className="h-4 w-4 text-green-600" />
                          ) : (
                            <BookOpen className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteLink(link.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                <LinkIcon className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No links found</h3>
                <p className="text-gray-500 mt-2 max-w-md">
                  No resource links match your search criteria. Try adjusting your search or add new links.
                </p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};

export default ContentManagement;
